import React, { useState, useEffect } from 'react';
import VersionDisplay from './VersionDisplay';
import { getErrorMessage } from '../utils/errorHandler';
import {
  corporationNameFromTenantPath,
  resolveCashFlowPdfFilename,
} from '../utils/reportPdfFilename';
import HojoSelect from './HojoSelect';

interface ReportOutputProps {
  apiBase?: string;
  onClose?: () => void;
  tenantName?: string;
  currentUser?: any;
  currentTenantId?: number | null;
  selectedYear?: number;
  initialSelectedReports?: string[];
  initialCategory?: string;
  categoryFilter?: string[];
}

interface ReportOption {
  id: string;
  name: string;
  endpoint?: string;
  pdfEndpoint?: string;
  csvEndpoint?: string;
  excelEndpoint?: string;
  enabled: boolean;
  category: string;
  description?: string;
  /** 資金収支 第一号第一/第四様式向け form_type */
  cashFlowFormType?: 'first' | 'fourth';
  /** 資金収支内訳書 第二/第三様式向け level */
  cashFlowBreakdownLevel?: 'jigyo' | 'kyoten';
}

interface AccessibleTenantOption {
  id: number;
  tenant_code?: string | null;
  tenant_name?: string | null;
  path?: string | null;
  tenant_type?: string | null;
}

interface UchiwakeShokeiMismatchItem {
  tenant_id: number;
  tenant_name: string;
  kyoten_name: string;
  kamoku_code: string;
  kamoku_name: string;
  parent_net: number;
  uchiwake_net: number;
  gap: number;
  null_hojo_amount: number;
  null_hojo_rows: number;
}

interface UchiwakeShokeiCheckResponse {
  has_mismatch: boolean;
  period_start: string;
  period_end: string;
  checked_tenant_ids: number[];
  mismatch_count: number;
  items: UchiwakeShokeiMismatchItem[];
  message: string;
}

const CASH_FLOW_REPORT_IDS = new Set([
  'cash-flow-first',
  'cash-flow-second',
  'cash-flow-third',
  'cash-flow',
]);

const formatYen = (n: number): string =>
  `${n.toLocaleString('ja-JP')}円`;


const ReportOutput: React.FC<ReportOutputProps> = ({ apiBase = '', onClose, tenantName, currentUser, currentTenantId, selectedYear, initialSelectedReports, initialCategory, categoryFilter }) => {
  const currentYear = selectedYear ?? new Date().getFullYear();
  void currentTenantId;
  void initialSelectedReports;
  void initialCategory;
  void categoryFilter;
  const [startDate, setStartDate] = useState<string>(`${currentYear}-04-01`);
  const [endDate, setEndDate] = useState<string>(`${currentYear + 1}-03-31`);

  useEffect(() => {
    if (selectedYear) {
      setStartDate(`${selectedYear}-04-01`);
      setEndDate(`${selectedYear + 1}-03-31`);
    }
  }, [selectedYear]);

  useEffect(() => {
    const loadFacilityTenants = async () => {
      const token = localStorage.getItem('auth_token') ?? localStorage.getItem('token');
      if (!token) {
        return;
      }
      try {
        const response = await fetch(`${apiBase}/api/tenants`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        // 現在のテナントのpathから法人名プレフィックスを取得
        // tenantName は "法人名 ＞ 事業区分 ＞ 施設名" または "法人名" の形式
        const currentCorpPrefix = tenantName ? tenantName.split(' ＞ ')[0] : null;
        const list: AccessibleTenantOption[] = (data.tenants || [])
          .filter(
            (t: AccessibleTenantOption & { id?: number | null }) => {
              if (!t.id || t.tenant_type !== 'FACILITY') return false;
              // 法人プレフィックスが取得できる場合は同一法人のみ
              if (currentCorpPrefix && t.path) {
                return t.path.startsWith(currentCorpPrefix);
              }
              return true; // フォールバック: プレフィックス取得不可の場合はFACILITYを全部表示
            },
          )
          .map((t: AccessibleTenantOption & { id: number }) => ({
            id: t.id,
            tenant_code: t.tenant_code,
            tenant_name: t.tenant_name,
            path: t.path,
            tenant_type: t.tenant_type,
          }));
        setFacilityTenants(list);
        if (list.length > 0) {
          setFacilityTenantId((prev) =>
            prev != null && list.some((t) => t.id === prev) ? prev : list[0].id,
          );
        }
      } catch (e) {
        console.warn('拠点テナント一覧の取得に失敗:', e);
      }
    };
    loadFacilityTenants();
  }, [apiBase, tenantName]);

  const [kaikeiKubun, setKaikeiKubun] = useState<number>(1);
  const [balanceSheetForm, setBalanceSheetForm] = useState<'first' | 'fourth'>('first');
  const [facilityTenantId, setFacilityTenantId] = useState<number | null>(null);
  const [facilityTenants, setFacilityTenants] = useState<AccessibleTenantOption[]>([]);
  /** 2026-08-10 Slack合意: 未チェック=0円行を隠す / チェック=すべて表示（既定は隠す） */
  const [showAllZeroRows, setShowAllZeroRows] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [uchiwakeWarning, setUchiwakeWarning] = useState<UchiwakeShokeiCheckResponse | null>(null);
  const [uchiwakeWarningLoading, setUchiwakeWarningLoading] = useState(false);
  const [uchiwakeWarningExpanded, setUchiwakeWarningExpanded] = useState(false);


  const [previewPdfs, setPreviewPdfs] = useState<{ name: string; downloadName: string; url: string }[]>([]);
  const [previewErrors, setPreviewErrors] = useState<string[]>([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [activePreviewIndex, setActivePreviewIndex] = useState(0);

  /** PDF 保存名。資金収支第一号様式は 年度_主体名_様式.pdf */
  const resolvePdfDownloadName = (report: ReportOption): string => {
    const corporationName = corporationNameFromTenantPath(tenantName);
    const facility = facilityTenants.find((t) => t.id === facilityTenantId);
    const facilityName = facility?.tenant_name ?? null;
    const formStyleName = resolveCashFlowPdfFilename(report, {
      periodEnd: endDate,
      corporationName,
      facilityName,
    });
    if (formStyleName) {
      return formStyleName;
    }
    return `${report.name}_${endDate}.pdf`;
  };

  const closePreviewModal = () => {
    setShowPreviewModal(false);
    previewPdfs.forEach(p => URL.revokeObjectURL(p.url));
    setTimeout(() => {
      setPreviewPdfs([]);
      setPreviewErrors([]);
    }, 100);
  };

  const buildBalanceSheetParams = (periodEnd: string): URLSearchParams => {
    const params = new URLSearchParams({
      period_end: periodEnd,
      form_type: balanceSheetForm,
    });
    if (balanceSheetForm === 'fourth' && facilityTenantId != null) {
      params.append('facility_tenant_id', facilityTenantId.toString());
    }
    return params;
  };

  /**
   * 資金収支・事業活動の共通クエリを付与する。
   * - hide_zero_amounts: 未チェック=true（隠す）/ チェック=false（すべて表示）
   * - 第一/第四: form_type、第二/第三: level
   */
  const applyCashFlowParams = (params: URLSearchParams, report: ReportOption): void => {
    // 2026-08-10 Slack合意 / BE既定(true) に合わせ、未チェック時はゼロ行を隠す
    params.set('hide_zero_amounts', (!showAllZeroRows).toString());
    if (report.cashFlowFormType) {
      params.set('form_type', report.cashFlowFormType);
      // 第四様式は拠点必須（グローバル拠点切替に依存しない）
      if (report.cashFlowFormType === 'fourth' && facilityTenantId != null) {
        params.set('facility_tenant_id', facilityTenantId.toString());
      }
    }
    if (report.cashFlowBreakdownLevel) {
      params.set('level', report.cashFlowBreakdownLevel);
    }
  };

  const validateBalanceSheetSelection = (selectedReportList: ReportOption[]): string | null => {
    if (!selectedReportList.some((r) => r.id === 'balance-sheet')) {
      return null;
    }
    if (balanceSheetForm === 'fourth' && facilityTenantId == null) {
      return '第四様式（拠点区分）を出力するには拠点を選択してください';
    }
    if (balanceSheetForm === 'fourth' && facilityTenants.length === 0) {
      return '選択可能な拠点がありません。テナントへのアクセス権限を確認してください';
    }
    return null;
  };

  const validateCashFlowSelection = (selectedReportList: ReportOption[]): string | null => {
    const needsFacility = selectedReportList.some(
      (r) => r.cashFlowFormType === 'fourth',
    );
    if (!needsFacility) {
      return null;
    }
    if (facilityTenantId == null) {
      return '第四様式（拠点区分）を出力するには拠点を選択してください';
    }
    if (facilityTenants.length === 0) {
      return '選択可能な拠点がありません。テナントへのアクセス権限を確認してください';
    }
    return null;
  };

  // 勘定科目リスト
  interface KamokuOption {
    id: number;
    kamoku_code: string;
    kamoku: string;
    hojo_kamoku_ari?: boolean;
  }
  const [kamokuList, setKamokuList] = useState<KamokuOption[]>([]);
  const [selectedKamokuId, setSelectedKamokuId] = useState<number | null>(null);
  const [selectedHojoId, setSelectedHojoId] = useState<string>('');

  // 勘定科目リストの取得
  useEffect(() => {
    const fetchKamoku = async () => {
      const token = localStorage.getItem('auth_token') ?? localStorage.getItem('token');
      if (!token) return;

      try {
        const response = await fetch(`${apiBase}/api/kamoku`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setKamokuList(data);
        }
      } catch (error) {
        console.error('勘定科目の取得に失敗しました', error);
      }
    };
    fetchKamoku();
  }, [apiBase]);

  // 帳票リスト（カテゴリ定義）
  const [reports] = useState<ReportOption[]>([
    // 財務諸表
    {
      id: 'trial-balance',
      name: '合計残高試算表（精算表）',
      endpoint: 'trial-balance',
      pdfEndpoint: 'trial-balance/pdf',
      enabled: true,
      category: '財務諸表',
      csvEndpoint: 'trial-balance/csv'
    },
    {
      id: 'cash-flow-first',
      name: '資金収支計算書（第一号第一様式）',
      endpoint: 'cash-flow-statement',
      pdfEndpoint: 'cash-flow-statement/pdf',
      enabled: true,
      category: '財務諸表',
      description: '法人単位',
      cashFlowFormType: 'first',
    },
    {
      id: 'cash-flow-second',
      name: '資金収支内訳書（第一号第二様式）',
      endpoint: 'cash-flow-statement/breakdown',
      pdfEndpoint: 'cash-flow-statement/breakdown/pdf',
      enabled: true,
      category: '財務諸表',
      description: '事業区分',
      cashFlowBreakdownLevel: 'jigyo',
    },
    {
      id: 'cash-flow-third',
      name: '資金収支内訳書（第一号第三様式）',
      endpoint: 'cash-flow-statement/breakdown',
      pdfEndpoint: 'cash-flow-statement/breakdown/pdf',
      enabled: true,
      category: '財務諸表',
      description: '拠点区分',
      cashFlowBreakdownLevel: 'kyoten',
    },
    {
      id: 'cash-flow',
      name: '資金収支計算書（第一号第四様式）',
      endpoint: 'cash-flow-statement',
      pdfEndpoint: 'cash-flow-statement/pdf',
      enabled: true,
      category: '財務諸表',
      description: '拠点区分',
      cashFlowFormType: 'fourth',
    },
    {
      id: 'business-activity',
      name: '事業活動計算書（第二号第一様式）',
      endpoint: 'business-activity-statement',
      pdfEndpoint: 'business-activity-statement/pdf',
      enabled: true,
      category: '財務諸表',
      description: '法人単位（子孫合算）',
      cashFlowFormType: 'first',
    },
    {
      id: 'business-activity-fourth',
      name: '事業活動計算書（第二号第四様式）',
      endpoint: 'business-activity-statement',
      pdfEndpoint: 'business-activity-statement/pdf',
      enabled: true,
      category: '財務諸表',
      description: '拠点区分',
      cashFlowFormType: 'fourth',
    },
    {
      id: 'balance-sheet',
      name: '貸借対照表（第3号様式）',
      endpoint: 'balance-sheet',
      pdfEndpoint: 'balance-sheet/pdf',
      enabled: true,
      category: '財務諸表'
    },
    {
      id: 'property-list',
      name: '財産目録（別紙4）',
      endpoint: 'property-list',
      pdfEndpoint: 'property-list/pdf',
      enabled: true,
      category: '財務諸表'
    },
    {
      id: 'annex-corporate',
      name: '附属明細書（法人全体）',
      endpoint: 'annex-corporate',
      pdfEndpoint: 'annex-corporate/pdf',
      enabled: true,
      category: '財務諸表'
    },
    {
      id: 'notes-output',
      name: '注記出力',
      endpoint: 'notes-output',
      pdfEndpoint: 'notes-print/pdf',
      enabled: true,
      category: '財務諸表'
    },

    // 予算管理
    {
      id: 'budget-summary',
      name: '予算執行状況月次推移表',
      endpoint: 'budget-summary',
      pdfEndpoint: '../budgets/monthly/pdf', // Updated endpoint for Budget PDF
      enabled: true,
      category: '予算管理'
    },
    {
      id: 'budget-variance',
      name: '予算実績差異分析表',
      pdfEndpoint: 'budget-variance/pdf',
      enabled: true,
      category: '予算管理'
    },

    // 帳簿・元帳
    {
      id: 'general-ledger',
      name: '総勘定元帳',
      endpoint: 'general-ledger',
      enabled: true,
      category: '帳簿・元帳'
    },
    {
      id: 'journal',
      name: '仕訳帳',
      enabled: true,
      category: '帳簿・元帳',
      csvEndpoint: 'journal/csv'
    },
    {
      id: 'sub-ledger',
      name: '補助元帳',
      endpoint: 'sub-ledger',
      enabled: true,
      category: '帳簿・元帳'
    },
    {
      id: 'journal-diary',
      name: '仕訳日記帳',
      pdfEndpoint: 'journal/pdf',
      enabled: true,
      category: '帳簿・元帳'
    },
    {
      id: 'deposit-schedule',
      name: '預金明細表',
      enabled: true,
      category: '帳簿・元帳',
      pdfEndpoint: 'deposit-ledger/pdf'
    },

    // 固定資産
    {
      id: 'fixed-asset-ledger',
      name: '固定資産台帳',
      pdfEndpoint: 'fixed-assets/ledger/pdf',
      enabled: true,
      category: '管理帳票'
    },
    {
      id: 'depreciation-schedule',
      name: '減価償却予定表',
      enabled: true,
      category: '管理帳票',
      pdfEndpoint: 'depreciation-schedule/pdf'
    },
    // 附属明細書
    {
      id: 'fixed-assets-schedule',
      name: '基本財産及びその他の固定資産の明細書（別紙6）',
      pdfEndpoint: 'annex/fixed-assets-schedule/pdf',
      enabled: true,
      category: '附属明細書'
    },
    {
      id: 'reserve-funds-schedule',
      name: '積立金・積立資産明細書（別紙8）',
      enabled: true,
      category: '附属明細書',
      pdfEndpoint: 'statutory/reserves/pdf'
    },
    {
      id: 'loans-schedule',
      name: '借入金明細書（別紙1）',
      pdfEndpoint: 'statutory/loans/pdf',
      enabled: true,
      category: '附属明細書'
    },
    {
      id: 'donations-schedule',
      name: '寄附金収益明細書（別紙2）',
      pdfEndpoint: 'annex/donations-schedule/pdf',
      enabled: true,
      category: '附属明細書'
    },
    {
      id: 'subsidies-schedule',
      name: '補助金収益明細書（別紙3）',
      pdfEndpoint: 'annex/subsidies-schedule/pdf',
      enabled: true,
      category: '附属明細書'
    },
    {
      id: 'transfers-schedule',
      name: '拠点区分間繰入金明細書（別紙4）',
      pdfEndpoint: 'annex/transfers-schedule/pdf',
      enabled: true,
      category: '附属明細書'
    },
    {
      id: 'basic-funds-schedule',
      name: '基本金明細書（別紙6）',
      pdfEndpoint: 'annex/basic-funds-schedule/pdf',
      enabled: true,
      category: '附属明細書'
    },
    {
      id: 'state-subsidy-reserve',
      name: '国庫補助金等特別積立金明細書（別紙7）',
      pdfEndpoint: 'annex/state-subsidy-reserve/pdf',
      enabled: true,
      category: '附属明細書'
    },

    // その他・決算書類
    {
      id: 'consumption-tax',
      name: '消費税関連勘定集計表（参考）',
      pdfEndpoint: 'consumption-tax/pdf',
      enabled: true,
      category: 'その他'
    },
    {
      id: 'notes',
      name: '財務諸表注記',
      pdfEndpoint: 'notes/pdf',
      enabled: true,
      category: 'その他'
    },
    {
      id: 'cover-sheet',
      name: '決算報告書表紙',
      enabled: true,
      category: 'その他',
      pdfEndpoint: 'statutory/cover/pdf'
    },
    {
      id: 'toc',
      name: '決算書目次',
      pdfEndpoint: 'toc/pdf',
      enabled: true,
      category: 'その他'
    },

    // CSV出力 (国提出)
    {
      id: 'wam-net-cf',
      name: '資金収支計算書（WAM NET CSV）',
      csvEndpoint: 'wam-net/cf',
      enabled: true,
      category: 'CSV出力'
    },
    {
      id: 'wam-net-pl',
      name: '事業活動計算書（WAM NET CSV）',
      csvEndpoint: 'wam-net/pl',
      enabled: true,
      category: 'CSV出力'
    },
    {
      id: 'wam-net-bs',
      name: '貸借対照表（WAM NET CSV）',
      csvEndpoint: 'wam-net/bs',
      enabled: true,
      category: 'CSV出力'
    },
    {
      id: 'audit-journal-entries',
      name: '監査法人提出用資料（全仕訳明細CSV）',
      csvEndpoint: 'audit/journal-entries/csv',
      enabled: true,
      category: 'CSV出力'
    }
  ]);

  const [selectedReports, setSelectedReports] = useState<Set<string>>(
    new Set() // デフォルト選択なし（試算表の初期チェック回帰を解消）
  );

  const isCashFlowSelected = Array.from(selectedReports).some((id) =>
    CASH_FLOW_REPORT_IDS.has(id),
  );

  useEffect(() => {
    if (!isCashFlowSelected || !startDate || !endDate) {
      setUchiwakeWarning(null);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      const token = localStorage.getItem('auth_token') ?? localStorage.getItem('token');
      if (!token) {
        return;
      }
      setUchiwakeWarningLoading(true);
      try {
        const params = new URLSearchParams({
          period_start: startDate,
          period_end: endDate,
        });
        // 第四様式のみ選択時は選択中の拠点に絞る（第一〜第三を含む場合は法人配下全体）
        const onlyFourth =
          selectedReports.has('cash-flow') &&
          !selectedReports.has('cash-flow-first') &&
          !selectedReports.has('cash-flow-second') &&
          !selectedReports.has('cash-flow-third');
        if (onlyFourth && facilityTenantId != null) {
          params.set('facility_tenant_id', facilityTenantId.toString());
        }
        const response = await fetch(
          `${apiBase}/api/reports/cash-flow-statement/uchiwake-shokei-check?${params}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!response.ok) {
          if (!cancelled) {
            setUchiwakeWarning(null);
          }
          return;
        }
        const data: UchiwakeShokeiCheckResponse = await response.json();
        if (!cancelled) {
          setUchiwakeWarning(data.has_mismatch ? data : null);
          if (!data.has_mismatch) {
            setUchiwakeWarningExpanded(false);
          }
        }
      } catch (e) {
        console.warn('内訳≠小計チェックに失敗:', e);
        if (!cancelled) {
          setUchiwakeWarning(null);
        }
      } finally {
        if (!cancelled) {
          setUchiwakeWarningLoading(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    apiBase,
    startDate,
    endDate,
    selectedReports,
    isCashFlowSelected,
    facilityTenantId,
  ]);


  const handleCheckboxChange = (reportId: string) => {
    const newSelected = new Set(selectedReports);
    if (newSelected.has(reportId)) {
      newSelected.delete(reportId);
    } else {
      newSelected.add(reportId);
    }
    setSelectedReports(newSelected);
  };

  const validateInputs = (): boolean => {
    setErrorMessage('');

    if (!startDate || !endDate) {
      setErrorMessage('対象期間を入力してください');
      return false;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setErrorMessage('開始日は終了日より前の日付を指定してください');
      return false;
    }

    if (selectedReports.size === 0) {
      setErrorMessage('帳票を1つ以上選択してください');
      return false;
    }

    // 補助元帳の場合は勘定科目が必須
    if (selectedReports.has('sub-ledger') && !selectedKamokuId) {
      setErrorMessage('補助元帳を出力する場合は、勘定科目を選択してください');
      return false;
    }

    return true;
  };

  const downloadPDF = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadCSV = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleGenerateReport = async (format: 'pdf' | 'csv' | 'excel' = 'pdf') => {
    if (format === 'excel') {
      const excelReports = reports.filter(r => selectedReports.has(r.id) && r.excelEndpoint);
      if (excelReports.length === 0) {
        // If clicked Excel but no report supports Excel (should normally be disabled or handled)
        // fallback or ignore. 
      }
    }
    if (!validateInputs()) {
      return;
    }

    setIsGenerating(true);
    setErrorMessage('');

    const token =
      localStorage.getItem('auth_token') ?? localStorage.getItem('token');
    if (!token) {
      setErrorMessage('認証情報が見つかりません。再度ログインしてください。');
      setIsGenerating(false);
      return;
    }

    try {
      // 選択された帳票を取得
      const selectedReportList = reports.filter(r =>
        selectedReports.has(r.id) && r.enabled
      );

      if (selectedReportList.length === 0) {
        setErrorMessage('実装済みの帳票を1つ以上選択してください');
        setIsGenerating(false);
        return;
      }

      const balanceSheetError = validateBalanceSheetSelection(selectedReportList);
      if (balanceSheetError) {
        setErrorMessage(balanceSheetError);
        setIsGenerating(false);
        return;
      }

      const cashFlowError = validateCashFlowSelection(selectedReportList);
      if (cashFlowError) {
        setErrorMessage(cashFlowError);
        setIsGenerating(false);
        return;
      }

      // CSV出力の場合のチェック
      if (format === 'csv') {
        const unsupported = selectedReportList.filter(r => !r.csvEndpoint);
        if (unsupported.length > 0) {
          setErrorMessage(`以下の帳票はCSV出力に対応していません: ${unsupported.map(r => r.name).join(', ')}`);
          setIsGenerating(false);
          return;
        }
      }

      const errors: string[] = [];
      let successCount = 0;

      for (const report of selectedReportList) {
        try {
          // 帳票タイプに応じてパラメータを設定
          let params: URLSearchParams;
          if (report.id === 'trial-balance') {
            if (startDate !== endDate) {
              params = new URLSearchParams({
                date_from: startDate,
                date_to: endDate,
                // 月別PDFも同一極性（未チェック=隠す）
                omit_zero: (!showAllZeroRows).toString(),
              });
            } else {
              params = new URLSearchParams({
                target_date: endDate,
                // omit_zero=true で隠す（未チェック時）。旧実装は極性逆転していた
                omit_zero: (!showAllZeroRows).toString(),
              });
            }
          } else if (report.id === 'balance-sheet') {
            params = buildBalanceSheetParams(endDate);
          } else if (report.id === 'depreciation-schedule' || report.id === 'property-list' || report.id === 'fixed-asset-ledger') {
            params = new URLSearchParams({
              period_end: endDate
            });
          } else if (report.id === 'sub-ledger' || report.id === 'general-ledger' || report.id === 'deposit-schedule') {
            // 元帳系：期間指定 + 必要なら勘定科目
            params = new URLSearchParams({
              start_date: startDate, // APIによってパラメータ名が異なる場合があるので注意
              end_date: endDate,     // sub-ledgerはstart_date/end_date
              period_start: startDate, // general-ledgerはperiod_start/period_end
              period_end: endDate
            });
            if (selectedKamokuId) {
              params.append('kamoku_id', selectedKamokuId.toString());
            }
            if (selectedHojoId) {
              params.append('hojo_id', selectedHojoId);
            }
          } else if (report.id === 'journal' || report.id === 'journal-diary') {
            params = new URLSearchParams({
              period_start: startDate,
              period_end: endDate
            });
          } else if (report.id === 'notes-output' || report.id === 'notes') {
            const end = new Date(endDate);
            const fiscalYear = end.getMonth() < 3 ? end.getFullYear() - 1 : end.getFullYear();
            params = new URLSearchParams({
              fiscal_year: fiscalYear.toString(),
              period_end: endDate,
            });
          } else if (report.category === '予算管理') {
            // 予算管理は年度(fiscal_year)を使用
            const end = new Date(endDate);
            // 3月までは前年度扱い (例: 2026-03-31 -> 2025年度)
            // getMonth() returns 0-11. 0=Jan, 2=March.
            const fiscalYear = end.getMonth() < 3 ? end.getFullYear() - 1 : end.getFullYear();

            params = new URLSearchParams({
              fiscal_year: fiscalYear.toString()
            });
          } else {
            // 資金収支計算書・事業活動収支計算書は period_start/period_end を使用
            params = new URLSearchParams({
              period_start: startDate,
              period_end: endDate
            });
            applyCashFlowParams(params, report);
          }

          const isCSV = format === 'csv' || !!report.category.includes('CSV');
          const isExcel = format === 'excel';
          let url: string;

          if (format === 'csv' && report.csvEndpoint) {
            url = `${apiBase}/api/reports/${report.csvEndpoint}?${params}`;
          } else if (format === 'excel' && report.excelEndpoint) {
            url = `${apiBase}/api/reports/${report.excelEndpoint}?${params}`;
          } else if (!!report.csvEndpoint && report.category === 'CSV出力') {
            url = `${apiBase}/api/reports/${report.csvEndpoint}?${params}`;
          } else {
            let pdfPath = report.pdfEndpoint ?? `${report.endpoint}/pdf`;
            if (report.id === 'trial-balance' && startDate !== endDate) {
              pdfPath = 'trial-balance/monthly-pdf';
            }
            url = `${apiBase}/api/reports/${pdfPath}?${params}`;
          }

          const response = await fetch(url, {
            method: 'GET',
            headers: {
              'Accept': isCSV ? 'text/csv' : isExcel ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf',
              'Authorization': `Bearer ${token}`
            }
          });

          if (!response.ok) {
            const errorDetail = await getErrorMessage(response, `${report.name}の生成に失敗しました`);
            throw new Error(errorDetail);
          }

          // ファイルをダウンロード
          const blob = await response.blob();

          if (isCSV) {
            // Filename extraction from header or fallback
            const disposition = response.headers.get('Content-Disposition');
            let filename = `${report.name}_${endDate}.csv`;
            if (disposition && disposition.indexOf('filename*=') !== -1) {
              const matches = /filename\*=UTF-8''(.+)/.exec(disposition);
              if (matches && matches[1]) {
                filename = decodeURIComponent(matches[1]);
              }
            }
            downloadCSV(blob, filename);
          } else if (isExcel) {
            const filename = `${report.name}_${endDate}.xlsx`;
            downloadCSV(blob, filename); // Reuse downloadCSV as it processes generic blob
          } else {
            // PDFかどうかを確認
            if (blob.type !== 'application/pdf' && blob.size > 0) {
              const text = await blob.text();
              console.error(`${report.name}: PDFではないレスポンス:`, text.substring(0, 500));
              throw new Error('PDFの取得に失敗しました。サーバーエラーの可能性があります。');
            }

            downloadPDF(blob, resolvePdfDownloadName(report));
          }

          console.log(`${report.name} を生成しました`);
          successCount++;

          // 複数帳票の場合は少し待機
          if (selectedReportList.length > 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }

        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : String(error);
          console.error(`${report.name}の生成に失敗しました:`, error);
          errors.push(`${report.name}: ${errorMsg}`);
        }
      }

      // 結果を表示
      if (errors.length === 0) {
        if (successCount === 1) {
          alert(`${selectedReportList[0].name}を生成しました`);
        } else {
          alert(`${successCount}件の帳票を生成しました`);
        }
      } else if (successCount > 0) {
        setErrorMessage(`${successCount}件成功、${errors.length}件失敗: ${errors.join('; ')}`);
        alert(`${successCount}件の帳票を生成しましたが、${errors.length}件の帳票でエラーが発生しました`);
      } else {
        setErrorMessage(`すべての帳票の生成に失敗しました: ${errors.join('; ')}`);
      }

    } catch (error) {
      console.error('帳票生成エラー:', error);
      setErrorMessage('帳票の生成中にエラーが発生しました');
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePreview = async () => {
    if (!validateInputs()) return;

    setIsGenerating(true);
    setErrorMessage('');
    setPreviewErrors([]);

    const token = localStorage.getItem('auth_token') ?? localStorage.getItem('token');
    if (!token) {
      setErrorMessage('認証情報が見つかりません');
      setIsGenerating(false);
      return;
    }

    try {
      const selectedReportList = reports.filter(r => selectedReports.has(r.id) && r.enabled);

      if (selectedReportList.length === 0) {
        setErrorMessage('実装済みの帳票を選択してください');
        setIsGenerating(false);
        return;
      }

      const balanceSheetError = validateBalanceSheetSelection(selectedReportList);
      if (balanceSheetError) {
        setErrorMessage(balanceSheetError);
        setIsGenerating(false);
        return;
      }

      const cashFlowError = validateCashFlowSelection(selectedReportList);
      if (cashFlowError) {
        setErrorMessage(cashFlowError);
        setIsGenerating(false);
        return;
      }

      const newPreviewPdfs: { name: string; downloadName: string; url: string }[] = [];
      const errors: string[] = [];

      for (const report of selectedReportList) {
        try {
          // 帳票タイプに応じてパラメータを設定
          let params: URLSearchParams;
          if (report.id === 'trial-balance') {
            if (startDate !== endDate) {
              params = new URLSearchParams({
                date_from: startDate,
                date_to: endDate,
                // 月別PDFも同一極性（未チェック=隠す）
                omit_zero: (!showAllZeroRows).toString(),
              });
            } else {
              params = new URLSearchParams({
                target_date: endDate,
                // omit_zero=true で隠す（未チェック時）。旧実装は極性逆転していた
                omit_zero: (!showAllZeroRows).toString(),
              });
            }
          } else if (report.id === 'balance-sheet') {
            params = buildBalanceSheetParams(endDate);
          } else if (report.id === 'depreciation-schedule' || report.id === 'property-list' || report.id === 'fixed-asset-ledger') {
            params = new URLSearchParams({ period_end: endDate });
          } else if (report.id === 'sub-ledger' || report.id === 'general-ledger' || report.id === 'deposit-schedule') {
            params = new URLSearchParams({
              start_date: startDate,
              end_date: endDate,
              period_start: startDate,
              period_end: endDate
            });
            if (selectedKamokuId) {
              params.append('kamoku_id', selectedKamokuId.toString());
            }
            if (selectedHojoId) {
              params.append('hojo_id', selectedHojoId);
            }
          } else if (report.id === 'journal' || report.id === 'journal-diary') {
            params = new URLSearchParams({
              period_start: startDate,
              period_end: endDate
            });
          } else if (report.id === 'notes-output' || report.id === 'notes') {
            const end = new Date(endDate);
            const fiscalYear = end.getMonth() < 3 ? end.getFullYear() - 1 : end.getFullYear();
            params = new URLSearchParams({
              fiscal_year: fiscalYear.toString(),
              period_end: endDate,
            });
          } else if (report.category === '予算管理') {
            const end = new Date(endDate);
            const fiscalYear = end.getMonth() < 3 ? end.getFullYear() - 1 : end.getFullYear();
            params = new URLSearchParams({ fiscal_year: fiscalYear.toString() });
          } else {
            params = new URLSearchParams({
              period_start: startDate,
              period_end: endDate
            });
            applyCashFlowParams(params, report);
          }

          let pdfPath = report.pdfEndpoint ?? `${report.endpoint}/pdf`;
          if (report.id === 'trial-balance' && startDate !== endDate) {
            pdfPath = 'trial-balance/monthly-pdf';
          }
          const url = `${apiBase}/api/reports/${pdfPath}?${params}`;

          const response = await fetch(url, {
            method: 'GET',
            headers: {
              'Accept': 'application/pdf',
              'Authorization': `Bearer ${token}`
            }
          });

          if (!response.ok) {
            const errorDetail = await getErrorMessage(response, `${report.name}のプレビュー取得に失敗しました`);
            throw new Error(errorDetail);
          }

          const blob = await response.blob();

          if (blob.type !== 'application/pdf' && blob.size > 0) {
            throw new Error('PDFの取得に失敗しました。サーバーエラーの可能性があります。');
          }

          const blobUrl = URL.createObjectURL(blob);
          newPreviewPdfs.push({
            name: report.name,
            downloadName: resolvePdfDownloadName(report),
            url: blobUrl,
          });

        } catch (err: any) {
          errors.push(`${report.name}: ${err.message}`);
        }
      }

      if (newPreviewPdfs.length > 0) {
        setPreviewPdfs(newPreviewPdfs);
        setPreviewErrors(errors);
        setActivePreviewIndex(0);
        setShowPreviewModal(true);
      }

      if (errors.length > 0) {
        setErrorMessage(`一部の帳票のプレビュー生成に失敗しました: ${errors.join(', ')}`);
      }

    } catch (error: any) {
      console.error('プレビューエラー:', error);
      setErrorMessage(error.message || 'プレビューの表示中にエラーが発生しました');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
      return;
    }
    window.history.back();
  };



  // カテゴリリストの取得（重複排除）
  const categories = Array.from(new Set(reports.map(r => r.category)));

  // アクティブなカテゴリの状態管理（初期値は最初のカテゴリ）
  const [activeCategory, setActiveCategory] = useState<string>(categories[0]);

  return (
    <div className="report-output-container">
      {/* ヘッダー */}
      <div
        style={{
          backgroundColor: '#0066cc',
          color: 'white',
          padding: '8px 16px',
          fontSize: '14px',
          fontWeight: 'bold',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}
      >
        <div>社会福祉法人　会計システム - 帳票出力</div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{tenantName}</div>
          <div style={{ fontSize: '10px' }}>ユーザー: {currentUser?.username || 'admin'}</div>
        </div>
      </div>

      <div className="classic-form-content">

        {/* 対象期間 */}
        <div className="form-section period-section">
          <div className="form-row">
            <label className="form-label">対象期間</label>
            <input
              type="date"
              className="classic-input date-input"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={isGenerating}
            />
            <span className="date-separator">～</span>
            <input
              type="date"
              className="classic-input date-input"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={isGenerating}
            />
          </div>

          <div className="form-row">
            <label className="form-label">会計区分</label>
            <select
              className="classic-select"
              value={kaikeiKubun}
              onChange={(e) => setKaikeiKubun(Number(e.target.value))}
              disabled={isGenerating}
            >
              <option value={1}>法人全体</option>
              <option value={2}>事業区分</option>
              <option value={3}>拠点区分</option>
            </select>
            <span style={{ marginLeft: 12, fontSize: 12, color: '#666' }}>
              ※帳票の選択自体は会計区分に依存しません（法人全体のままで選択可）
            </span>
          </div>

          {selectedReports.has('balance-sheet') && (
            <>
              <div className="form-row">
                <label className="form-label">貸借対照表様式</label>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="balanceSheetForm"
                      checked={balanceSheetForm === 'first'}
                      onChange={() => setBalanceSheetForm('first')}
                      disabled={isGenerating}
                    />
                    第一様式（法人全体）
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="balanceSheetForm"
                      checked={balanceSheetForm === 'fourth'}
                      onChange={() => setBalanceSheetForm('fourth')}
                      disabled={isGenerating}
                    />
                    第四様式（拠点区分）
                  </label>
                </div>
              </div>
            </>
          )}

          {/* 貸借第四 or 資金収支第四で拠点選択を表示（それ以外では拠点切替不要） */}
          {((selectedReports.has('balance-sheet') && balanceSheetForm === 'fourth') ||
            selectedReports.has('cash-flow')) && (
            <div className="form-row">
              <label className="form-label">拠点</label>
              <select
                className="classic-select"
                value={facilityTenantId ?? ''}
                onChange={(e) =>
                  setFacilityTenantId(e.target.value ? Number(e.target.value) : null)
                }
                disabled={isGenerating || facilityTenants.length === 0}
                style={{ minWidth: '280px' }}
              >
                {facilityTenants.length === 0 ? (
                  <option value="">（拠点がありません）</option>
                ) : (
                  facilityTenants.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.tenant_name}
                    </option>
                  ))
                )}
              </select>
              <span style={{ marginLeft: 12, fontSize: 12, color: '#666' }}>
                ※第四様式（貸借／資金収支）の出力時のみ必須
              </span>
            </div>
          )}

          {/* 勘定科目選択（補助元帳・総勘定元帳・預金明細表用） */}
          {(selectedReports.has('sub-ledger') || selectedReports.has('general-ledger') || selectedReports.has('deposit-schedule')) && (
            <div className="form-row">
              <label className="form-label">勘定科目</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
                <select
                  className="classic-select"
                  value={selectedKamokuId || ''}
                  onChange={(e) => {
                    setSelectedKamokuId(e.target.value ? Number(e.target.value) : null);
                    setSelectedHojoId(''); // 科目変更時は補助科目をリセット
                  }}
                  disabled={isGenerating}
                >
                  <option value="">（指定なし）</option>
                  {kamokuList.map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.kamoku_code} {k.kamoku}
                    </option>
                  ))}
                </select>
                {selectedKamokuId && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '13px', color: '#555' }}>補助科目:</span>
                    <HojoSelect
                      kamokuId={selectedKamokuId}
                      value={selectedHojoId ? parseInt(selectedHojoId, 10) : null}
                      onChange={(hojoId) => setSelectedHojoId(hojoId)}
                      apiFetch={async (url, options) => {
                        const token = localStorage.getItem('auth_token') ?? localStorage.getItem('token');
                        return fetch(`${apiBase}${url}`, {
                          ...options,
                          headers: { ...options?.headers, 'Authorization': `Bearer ${token}` }
                        });
                      }}
                      hasHojo={kamokuList.find(k => k.id === selectedKamokuId)?.hojo_kamoku_ari ?? true}
                      style={{ width: '200px', height: '32px' }}
                      disabled={isGenerating}
                    />
                  </div>
                )}
              </div>
              {selectedReports.has('sub-ledger') && (
                <span className="helper-text ml-2 text-sm text-red-600">※補助元帳のみ必須</span>
              )}
            </div>
          )}
        </div>

        {/* 帳票選択セクション（タブ切り替え） */}
        <div className="form-section report-section">
          <h3 className="section-title">帳票選択</h3>

          {/* タブヘッダー */}
          <div className="tabs-header">
            {categories.map((category) => (
              <button
                key={category}
                className={`tab-button ${activeCategory === category ? 'active' : ''}`}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>

          <div className="report-list-container">
            {/* 選択されたカテゴリの帳票のみ表示 */}
            <div className="report-category-group">
              {/* カテゴリごとの説明などを入れる場合はここに */}
              <div className="report-grid">
                {reports
                  .filter(r => r.category === activeCategory)
                  .map((report) => (
                    <label
                      key={report.id}
                      className={`checkbox-label ${!report.enabled ? 'disabled' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedReports.has(report.id)}
                        onChange={() => handleCheckboxChange(report.id)}
                        disabled={!report.enabled || isGenerating}
                      />
                      <span className="checkbox-text">
                        {report.name}
                        {!report.enabled && (
                          <span className="badge-disabled">
                            {report.description || '未実装'}
                          </span>
                        )}
                        {report.enabled && report.description && (
                          <span className="badge-info">
                            {report.description}
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
              </div>
            </div>
          </div>

          {/* 出力オプション */}
          <div className="output-options">
            <h4 className="subsection-title">出力オプション</h4>

            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={showAllZeroRows}
                onChange={(e) => setShowAllZeroRows(e.target.checked)}
                disabled={isGenerating}
              />
              <span className="checkbox-text">０円の行もすべて表示する</span>
            </label>
          </div>
        </div>

        {/* 資金収支 内訳≠小計 警告（PDFレイアウトは変更しない） */}
        {isCashFlowSelected && uchiwakeWarningLoading && (
          <div
            style={{
              marginBottom: '12px',
              padding: '10px 14px',
              backgroundColor: '#f5f5f5',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '13px',
              color: '#555',
            }}
          >
            資金収支の内訳・小計の整合性を確認しています…
          </div>
        )}
        {isCashFlowSelected && uchiwakeWarning && uchiwakeWarning.has_mismatch && (
          <div
            role="alert"
            style={{
              marginBottom: '12px',
              padding: '12px 14px',
              backgroundColor: '#fff8e6',
              border: '1px solid #e6b800',
              borderLeft: '4px solid #e6b800',
              borderRadius: '4px',
              color: '#5c4b00',
              fontSize: '13px',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: '6px' }}>
              ⚠ 資金収支：内訳と小計が一致しない科目があります（{uchiwakeWarning.mismatch_count}件）
            </div>
            <div style={{ marginBottom: '8px' }}>{uchiwakeWarning.message}</div>
            <ul style={{ margin: '0 0 8px 18px', padding: 0 }}>
              {(uchiwakeWarningExpanded
                ? uchiwakeWarning.items
                : uchiwakeWarning.items.slice(0, 5)
              ).map((item, idx) => (
                <li key={`${item.tenant_id}-${item.kamoku_code}-${idx}`} style={{ marginBottom: '4px' }}>
                  [{item.kyoten_name || item.tenant_name}] {item.kamoku_code} {item.kamoku_name}
                  ：小計 {formatYen(item.parent_net)} / 内訳 {formatYen(item.uchiwake_net)}
                  （差 {formatYen(item.gap)}）
                </li>
              ))}
            </ul>
            {uchiwakeWarning.items.length > 5 && (
              <button
                type="button"
                onClick={() => setUchiwakeWarningExpanded((v) => !v)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#0052a3',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: '13px',
                  textDecoration: 'underline',
                }}
              >
                {uchiwakeWarningExpanded
                  ? '件数を減らして表示'
                  : `残り ${uchiwakeWarning.items.length - 5} 件を表示`}
              </button>
            )}
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#7a6a30' }}>
              帳票PDFのレイアウト自体は変わりません。補助科目の付け漏れなどをご確認ください。
            </div>
          </div>
        )}

        {/* エラーメッセージ */}
        {errorMessage && (
          <div className="error-message">
            ⚠️ {errorMessage}
          </div>
        )}

        {/* 実行ボタン */}
        <div className="action-buttons">
          <button
            className="btn-primary"
            onClick={() => handleGenerateReport('pdf')}
            disabled={isGenerating}
          >
            {isGenerating ? '処理中...' : 'PDF出力'}
          </button>

          <button
            className="btn-success"
            onClick={() => handleGenerateReport('csv')}
            disabled={isGenerating}
            style={{
              marginLeft: '10px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {isGenerating ? '処理中...' : 'CSV出力'}
          </button>

          <button
            className="btn-secondary"
            onClick={() => handleGenerateReport('excel')}
            disabled={isGenerating || !reports.some(r => selectedReports.has(r.id) && r.excelEndpoint)}
            style={{
              marginLeft: '10px',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: 'pointer',
              opacity: reports.some(r => selectedReports.has(r.id) && r.excelEndpoint) ? 1 : 0.5
            }}
          >
            EXCEL出力
          </button>

          <button
            className="btn-secondary"
            onClick={handlePreview}
            disabled={isGenerating}
          >
            プレビュー
          </button>

          <button
            className="btn-default"
            onClick={handleClose}
            disabled={isGenerating}
          >
            閉じる
          </button>
        </div>
      </div>

      {/* バージョン表示 */}
      <VersionDisplay position="footer" showDetails={false} />

      <style>{`
        .report-output-container {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .classic-form-content {
          background: white;
          padding: 30px;
          border-radius: 4px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .form-section {
          margin-bottom: 30px;
          padding: 20px;
          border: 1px solid #ddd;
          border-radius: 4px;
          background: #fafafa;
        }

        .period-section {
          background: #f5f5f5;
        }

        .form-row {
          display: flex;
          align-items: center;
          margin-bottom: 15px;
          gap: 10px;
        }

        .form-label {
          font-weight: bold;
          min-width: 120px;
          font-size: 14px;
        }

        .classic-input,
        .classic-select {
          padding: 8px 12px;
          border: 1px solid #ccc;
          border-radius: 4px;
          font-size: 14px;
          background: white;
        }

        .date-input {
          width: 150px;
        }

        .date-separator {
          margin: 0 10px;
          font-size: 16px;
        }

        .classic-select {
          width: 200px;
        }

        .section-title {
          font-size: 18px;
          font-weight: bold;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 2px solid #333;
        }

        .report-list-container {
          display: flex;
          flex-direction: column;
          gap: 25px;
        }

        .category-header {
          font-size: 15px;
          font-weight: bold;
          color: #555;
          margin-bottom: 10px;
          padding-left: 5px;
          border-left: 3px solid #007bff;
        }

        /* Tabs Styles */
        .tabs-header {
          display: flex;
          gap: 5px;
          border-bottom: 2px solid #ddd;
          margin-bottom: 20px;
          overflow-x: auto;
          padding-bottom: 2px;
        }

        .tab-button {
          padding: 8px 16px;
          border: 1px solid transparent;
          border-bottom: none;
          background: transparent;
          cursor: pointer;
          font-size: 14px;
          color: #666;
          border-radius: 4px 4px 0 0;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .tab-button:hover {
          background-color: #f0f0f0;
          color: #333;
        }

        .tab-button.active {
          background-color: #fff;
          border-color: #ddd;
          border-bottom-color: #fff;
          color: #007bff;
          font-weight: bold;
          margin-bottom: -2px; /* Access border overlap */
          z-index: 1;
        }

        .report-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 10px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          cursor: pointer;
          padding: 10px;
          border-radius: 4px;
          background: white;
          border: 1px solid #eee;
          transition: all 0.2s;
        }

        .checkbox-label:hover:not(.disabled) {
          background: #e8f0fe;
          border-color: #b3d7ff;
        }

        .checkbox-label.disabled {
          opacity: 0.6;
          cursor: not-allowed;
          background: #f9f9f9;
        }

        .checkbox-label input[type="checkbox"] {
          margin-right: 10px;
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .checkbox-label.disabled input[type="checkbox"] {
          cursor: not-allowed;
        }

        .checkbox-text {
          font-size: 14px;
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px;
        }

        .badge-disabled {
          padding: 2px 6px;
          background: #6c757d;
          color: white;
          border-radius: 3px;
          font-size: 10px;
        }

        .badge-info {
          padding: 2px 6px;
          background: #17a2b8;
          color: white;
          border-radius: 3px;
          font-size: 10px;
        }

        .output-options {
          margin-top: 25px;
          padding-top: 15px;
          border-top: 1px solid #ddd;
        }

        .subsection-title {
          font-size: 14px;
          font-weight: bold;
          margin-bottom: 10px;
        }

        .error-message {
          margin: 20px 0;
          padding: 15px;
          background: #fff3cd;
          border: 1px solid #ffc107;
          border-radius: 4px;
          color: #856404;
          font-size: 14px;
        }

        .action-buttons {
          display: flex;
          gap: 15px;
          justify-content: center;
          margin-top: 30px;
          padding-top: 20px;
          border-top: 2px solid #ddd;
        }

        .btn-primary,
        .btn-secondary,
        .btn-default {
          padding: 12px 30px;
          font-size: 15px;
          font-weight: bold;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
          min-width: 150px;
        }

        .btn-primary {
          background: #007bff;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: #0056b3;
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #545b62;
        }

        .btn-default {
          background: #f8f9fa;
          color: #333;
          border: 1px solid #ccc;
        }

        .btn-default:hover:not(:disabled) {
          background: #e2e6ea;
        }

        .btn-primary:disabled,
        .btn-secondary:disabled,
        .btn-default:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>

      {/* 帳票プレビューモーダル（1画面1種類。複数件はタブ切替。結合しない） */}
      {showPreviewModal && previewPdfs.length > 0 && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 9999,
          display: 'flex', flexDirection: 'column',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '8px',
            display: 'flex', flexDirection: 'column', height: '100%',
            overflow: 'hidden', boxShadow: '0 10px 25px rgba(0,0,0,0.5)'
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 20px', borderBottom: '1px solid #bcd4f0',
              backgroundColor: '#e8f1fc'
            }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: '#0052a3', fontWeight: 700 }}>
                帳票一括プレビュー
                <span style={{
                  display: 'inline-block', marginLeft: '10px', padding: '2px 10px',
                  borderRadius: '12px', backgroundColor: '#0066cc', color: 'white',
                  fontSize: '14px', fontWeight: 700, verticalAlign: 'middle'
                }}>
                  {activePreviewIndex + 1} / {previewPdfs.length}件
                </span>
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  type="button"
                  onClick={async () => {
                    const pdf = previewPdfs[activePreviewIndex];
                    if (!pdf) return;
                    const response = await fetch(pdf.url);
                    const blob = await response.blob();
                    const filename = pdf.downloadName.toLowerCase().endsWith('.pdf')
                      ? pdf.downloadName
                      : `${pdf.downloadName}.pdf`;
                    downloadPDF(blob, filename);
                  }}
                  style={{
                    padding: '6px 14px', fontSize: '14px', fontWeight: 600,
                    backgroundColor: '#0066cc', color: 'white', border: 'none',
                    borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap'
                  }}
                >
                  このPDFを保存
                </button>
                <button
                  onClick={closePreviewModal}
                  style={{
                    background: 'none', border: 'none', fontSize: '28px', cursor: 'pointer',
                    padding: '0 8px', color: '#666', lineHeight: '1'
                  }}
                >×</button>
              </div>
            </div>

            {uchiwakeWarning && uchiwakeWarning.has_mismatch && (
              <div style={{
                padding: '10px 16px', backgroundColor: '#fff8e6', color: '#5c4b00',
                fontSize: '13px', borderBottom: '1px solid #e6d9a8'
              }}>
                ⚠ 資金収支で内訳≠小計の科目が {uchiwakeWarning.mismatch_count} 件あります
                （例: {(uchiwakeWarning.items[0]?.kyoten_name || uchiwakeWarning.items[0]?.tenant_name || '')}
                {' '}{uchiwakeWarning.items[0]?.kamoku_code} {uchiwakeWarning.items[0]?.kamoku_name}
                差 {formatYen(uchiwakeWarning.items[0]?.gap || 0)}）。
                PDFレイアウトは変更していません。
              </div>
            )}

            {previewPdfs.length > 1 && (
              <>
                <div style={{
                  padding: '8px 16px', backgroundColor: '#fff8e6', color: '#5c4b00',
                  fontSize: '13px', borderBottom: '1px solid #e6d9a8'
                }}>
                  他の帳票は上のタブから切り替えできます（結合せず、1画面に1種類ずつ表示します）。
                </div>
                <div style={{
                  display: 'flex', overflowX: 'auto', borderBottom: '3px solid #0066cc',
                  backgroundColor: '#d6e8fb', padding: '8px 10px', gap: '8px', alignItems: 'center'
                }}>
                  {previewPdfs.map((pdf, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setActivePreviewIndex(idx)}
                      style={{
                        padding: '10px 16px', cursor: 'pointer', borderRadius: '6px 6px 0 0',
                        border: activePreviewIndex === idx ? '2px solid #0066cc' : '1px solid #90b8e8',
                        borderBottom: activePreviewIndex === idx ? '2px solid white' : '1px solid #90b8e8',
                        backgroundColor: activePreviewIndex === idx ? 'white' : '#f0f6ff',
                        fontWeight: activePreviewIndex === idx ? 700 : 500,
                        color: activePreviewIndex === idx ? '#0052a3' : '#334',
                        whiteSpace: 'nowrap', outline: 'none', fontSize: '13px'
                      }}
                    >
                      {idx + 1}. {pdf.name}
                    </button>
                  ))}
                </div>
              </>
            )}

            {previewErrors.length > 0 && (
              <div style={{
                padding: '10px 16px', backgroundColor: '#fdecea', color: '#8a1f11',
                fontSize: '13px', borderBottom: '1px solid #f5c6cb'
              }}>
                次の帳票はプレビューできませんでした:
                <ul style={{ margin: '6px 0 0 18px' }}>
                  {previewErrors.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
              </div>
            )}

            <div style={{ flex: 1, backgroundColor: '#525659' }}>
              <iframe
                src={previewPdfs[activePreviewIndex].url}
                style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                title="PDF Preview"
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ReportOutput;
